// The flow engine in shadow on the flow page (7.2 M3). Shadow decides, step by
// step, what Klaviyo's flow would have sent, and sends nothing: a decision is
// a row, and there is no live mode to switch to.
//
// Three pieces, so the detail page changes in a few lines only:
// `<FlowShadow>` wraps the page and loads the numbers once; `<FlowShadowCard>`
// is the switch (admin only, with a reason) and what the engine says about
// the flow — what holds it up, how far it has decided, its runs by state;
// `<StepShadowBadge>` is one step's numbers beside its `#id` in the outline.
// Outside `<FlowShadow>` the badge draws nothing (the outline is also drawn
// on its own, in the tests).

import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, fetchFlowShadowStats, setFlowMode } from "@/lib/api";
import type { FlowDetail as FlowDetailData, FlowMode, FlowShadowStats, FlowShadowStepStats } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLoad } from "@/lib/useLoad";
import type { Loaded } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect } from "@/components/ui/native-select";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DetailList } from "@/components/emc/DetailList";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState, LoadingState } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { runsByState, statsByStep, stepBreakdown, stepSummary } from "@/pages/flows/shadowModel";

// The API's limits on the reason (`ModeIn`).
const REASON_MIN = 3;
const REASON_MAX = 500;

/** The window the per-step numbers count: all of it, or the last n days. */
const RANGES = [
  { value: "", label: "Az összes", days: null },
  { value: "1", label: "Az utolsó 24 óra", days: 1 },
  { value: "7", label: "Az utolsó 7 nap", days: 7 },
  { value: "30", label: "Az utolsó 30 nap", days: 30 },
] as const;

interface ShadowState {
  flow: FlowDetailData;
  onChanged: () => void;
  stats: Loaded<FlowShadowStats>;
  byStep: Record<string, FlowShadowStepStats> | null;
  reload: () => void;
  replay: boolean;
  setReplay: (replay: boolean) => void;
  range: string;
  setRange: (range: string) => void;
}

const ShadowContext = createContext<ShadowState | null>(null);

function sinceOf(range: string): string | undefined {
  const days = RANGES.find((each) => each.value === range)?.days ?? null;
  return days === null ? undefined : new Date(Date.now() - days * 86_400_000).toISOString();
}

/** The flow page inside it: the shadow numbers, loaded once for the card and
 * every step's badge. `onChanged` reloads the flow after a switch. */
export function FlowShadow({
  flow,
  onChanged,
  children,
}: {
  flow: FlowDetailData;
  onChanged: () => void;
  children: ReactNode;
}) {
  const [replay, setReplay] = useState(false);
  const [range, setRange] = useState("");
  const [stats, reload] = useLoad(
    () => fetchFlowShadowStats(flow.id, { replay, since: sinceOf(range) }),
    [flow.id, flow.mode, flow.shadow_since, replay, range],
  );
  const byStep = stats.data === null ? null : statsByStep(stats.data);
  return (
    <ShadowContext.Provider
      value={{ flow, onChanged, stats, byStep, reload, replay, setReplay, range, setRange }}
    >
      <div>{children}</div>
    </ShadowContext.Provider>
  );
}

/** One step's numbers, beside its `#id`: "123 futás · küldené 80 · kihagyná 40";
 * the reasons on hover. Nothing before the numbers are in, or when the engine
 * has never reached any step of the flow. */
export function StepShadowBadge({ id }: { id: string }) {
  const shadow = useContext(ShadowContext);
  if (shadow === null || shadow.byStep === null || Object.keys(shadow.byStep).length === 0) return null;
  const step = shadow.byStep[id];
  const reasons = stepBreakdown(step);
  return (
    <span
      data-slot="step-shadow"
      title={reasons.length > 0 ? reasons.join("\n") : undefined}
      className="data-mono rounded-pill bg-fill-secondary px-2 text-label font-medium text-muted-foreground"
    >
      {stepSummary(step)}
    </span>
  );
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

function ModeSwitch({ flow, onChanged }: { flow: FlowDetailData; onChanged: () => void }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const target: FlowMode = flow.mode === "shadow" ? "off" : "shadow";
  const admin = user?.role === "admin";
  const label = target === "shadow" ? "Árnyékba kapcsolás" : "Árnyék kikapcsolása";
  return (
    <>
      <Button
        type="button"
        variant={target === "shadow" ? "default" : "outline"}
        disabled={!admin}
        title={admin ? undefined : "Csak admin kapcsolhatja."}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <ActionDialog
        open={open}
        onOpenChange={setOpen}
        title={label}
        description={
          target === "shadow" ? (
            <>
              A motor ettől a pillanattól minden indító-jelet felvesz, és lépésenként eldönti, kinek menne
              levél és miért, de <strong>levelet nem küld</strong>. A korábbi jelekből visszatöltött múlt
              külön számít.
            </>
          ) : (
            <>
              A motor nem vesz fel új jelet, és nem lép tovább a futásokkal. A futások és döntések
              megmaradnak; visszakapcsolva onnan folytatódnak, ahol álltak.
            </>
          )
        }
        confirmLabel={label}
        reason={{
          label: "Miért kapcsolod át?",
          placeholder: target === "shadow" ? "pl. a BF előtti összevetés" : "pl. hibás döntéseket hoz",
          max: REASON_MAX,
        }}
        onConfirm={async (reason) => {
          if (reason.length < REASON_MIN) {
            throw new Error(`Az indoklás legalább ${REASON_MIN} karakter legyen.`);
          }
          try {
            await setFlowMode(flow.id, target, reason);
          } catch (error: unknown) {
            if (error instanceof ApiError && error.status === 409) {
              onChanged();
              throw new Error(
                `A flow már ${target === "shadow" ? "árnyékban van" : "ki van kapcsolva"}: közben valaki átkapcsolta. Az oldal frissült. (${error.message})`,
              );
            }
            throw new Error(messageOf(error));
          }
          onChanged();
        }}
      />
    </>
  );
}

function EngineStatus({ stats }: { stats: FlowShadowStats }) {
  const engine = stats.engine;
  const states = runsByState(stats.runs_by_state);
  return (
    <div className="flex flex-col gap-3">
      {engine.blocked.length > 0 && (
        <Callout tone="warn">
          <p className="m-0 font-medium">A motor most nem tud dönteni ennél a flow-nál:</p>
          <ul className="m-0 mt-1 pl-5">
            {engine.blocked.map((block, index) => (
              <li key={`${block.code}-${index}`}>
                {block.text}
                {block.stream ? <span className="data-mono text-muted-foreground"> · {block.stream}</span> : null}
              </li>
            ))}
          </ul>
        </Callout>
      )}
      <DetailList
        columns={2}
        items={[
          { label: "Eddig dönthet (vízjel)", value: minute(engine.watermark), mono: true },
          { label: "Utolsó ellenőrzés", value: minute(engine.checked_at), mono: true, muted: true },
          { label: "Felvett indító-jelek", value: count(engine.taken_in), mono: true },
          { label: "Átugrott jelek (profil nélkül)", value: count(engine.passed_over), mono: true, muted: true },
          { label: "Felvétel kezdete", value: minute(engine.intake_from), mono: true },
          { label: "Korábbi, már nem döntő jelek", value: count(engine.out_of_bound), mono: true, muted: true },
        ]}
      />
      <div className="flex flex-col gap-1.5">
        <p className="caps-label m-0">Futások állapot szerint</p>
        {states.length === 0 ? (
          <p className="m-0 text-muted-foreground">Még nincs futás.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {states.map((each) => (
              <StatusPill key={each.state} tone={each.tone} title={each.state}>
                {each.label} · {count(each.count)}
              </StatusPill>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** The switch and the engine's view of the flow. Inside `<FlowShadow>` only. */
export function FlowShadowCard() {
  const shadow = useContext(ShadowContext);
  if (shadow === null) return null;
  const { flow, onChanged, stats, reload, replay, setReplay, range, setRange } = shadow;
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Árnyék-mód</CardTitle>
        <CardDescription>
          Árnyékban a motor a flow minden lépésén dönt (kinek menne levél, kinek nem, és miért), de levelet
          nem küld. A döntések a Klaviyo valódi küldéseivel vethetők össze.
        </CardDescription>
        <CardAction>
          <ModeSwitch flow={flow} onChanged={onChanged} />
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="m-0 flex flex-wrap items-center gap-2">
          Mód: <StatusPill domain="flowMode" state={flow.mode} />
          {flow.mode === "shadow" ? (
            <span className="text-muted-foreground">
              árnyékban <span className="data-mono">{minute(flow.shadow_since)}</span> óta
            </span>
          ) : (
            <span className="text-muted-foreground">a motor ezzel a flow-val nem foglalkozik</span>
          )}
        </p>
        {stats.error !== null && <ErrorState message={stats.error} onRetry={reload} />}
        {stats.data === null ? stats.error === null && <LoadingState lines={2} /> : <EngineStatus stats={stats.data} />}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line pt-3">
          <span className="text-detail text-muted-foreground">A lépések melletti számok:</span>
          <NativeSelect
            size="sm"
            aria-label="Időszak"
            value={range}
            onChange={(event) => setRange(event.target.value)}
          >
            {RANGES.map((each) => (
              <option key={each.value} value={each.value}>
                {each.label}
              </option>
            ))}
          </NativeSelect>
          <label className="flex cursor-pointer items-center gap-2 text-detail">
            <Checkbox checked={replay} onChange={(event) => setReplay(event.target.checked)} />
            visszatöltött múlttal együtt
          </label>
          <Button type="button" variant="ghost" size="sm" onClick={reload}>
            Frissítés
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
